import { useEffect, useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import { useAppSelector } from '../../_store/hooks';
import { useGetDashboardInfos } from '@/_features/dashboard/api/get-dashboard-infos';
import {
  ENUM_DELIVERY_MODEL_LABELS,
  ENUM_LEGEND_STATUS,
} from '@/_features/dashboard/constants';
import {
  DELIVERY_MODEL_TYPE,
  type OnlineDeliveryman,
  type PlacesWithLocation,
} from '@/_features/dashboard/types';
import { MarkerDeliveryman } from '@/_features/dashboard/components/markerDeliveryman';
import { MarkerPlace } from '@/_features/dashboard/components/markerPlace';
import { useCities } from '@/_hooks/use-cities';
import { Skeleton } from '@/_config/lib/react-native-skeleton';

const MotoboysMap = ({ filters }: { filters: string[] }) => {
  const { cities } = useCities();
  const selectedCityOnDashboard = useAppSelector(
    state => state.filters.cityIdOnDashboard,
  );

  const selectedsModelDelivery = useAppSelector(
    state => state.deliveryModel.selectedsDeliveryModel,
  );

  const selectedCityLocation = cities?.find(
    city => city._id === selectedCityOnDashboard,
  )?.localizacao;

  const { data } = useGetDashboardInfos(
    {
      cityId: selectedCityOnDashboard || '',
    },
    {
      enabled: !!selectedCityOnDashboard,
    },
  );

  const { onlineDeliverymen = [], placesWithLocation = [] } = data || {};

  const hasFilter = (filter: string) => filters.includes(filter);
  const hasAnyFilter = filters.length > 0;

  const includesModel = (label: ENUM_DELIVERY_MODEL_LABELS) =>
    selectedsModelDelivery.includes(label);
  const hasAllModel =
    selectedsModelDelivery.includes(ENUM_DELIVERY_MODEL_LABELS.ALL) ||
    selectedsModelDelivery.length === 0;

  const statusFilters = {
    inProgress: hasFilter(ENUM_LEGEND_STATUS.PLACE_WITH_DELIVERY_IN_PROGRESS),
    pending: hasFilter(ENUM_LEGEND_STATUS.PLACE_WITH_DELIVERY_PENDING),
  };

  const modelFilters = {
    ownDeliveryman: includesModel(ENUM_DELIVERY_MODEL_LABELS.OWN_DELIVERYMAN),
    cloudDeliveryman: includesModel(
      ENUM_DELIVERY_MODEL_LABELS.CLOUD_DELIVERYMAN,
    ),
    bothDeliveryman: includesModel(
      ENUM_DELIVERY_MODEL_LABELS.OWN_AND_CLOUD_DELIVERYMAN,
    ),

    ownPlace: includesModel(ENUM_DELIVERY_MODEL_LABELS.OWN_PLACE),
    cloudPlace: includesModel(ENUM_DELIVERY_MODEL_LABELS.CLOUD_PLACE),
    bothPlace: includesModel(ENUM_DELIVERY_MODEL_LABELS.OWN_AND_CLOUD_PLACE),
  };

  const matchPlace = (place: PlacesWithLocation) =>
    (statusFilters.inProgress && place.inProgressDeliveriesCount > 0) ||
    (statusFilters.pending && place.pendingDeliveriesCount > 0);

  const matchDeliveryman = (deliveryman: OnlineDeliveryman) =>
    hasAnyFilter ? filters.includes(deliveryman.status) : true;

  const deliverymanRules = {
    [DELIVERY_MODEL_TYPE.LINKED]: modelFilters.ownDeliveryman,
    [DELIVERY_MODEL_TYPE.CLOUD]: modelFilters.cloudDeliveryman,
    [DELIVERY_MODEL_TYPE.CLOUD_AND_LINKED]: modelFilters.bothDeliveryman,
  };

  const placeRules = {
    [DELIVERY_MODEL_TYPE.LINKED]: modelFilters.ownPlace,
    [DELIVERY_MODEL_TYPE.CLOUD]: modelFilters.cloudPlace,
    [DELIVERY_MODEL_TYPE.CLOUD_AND_LINKED]: modelFilters.bothPlace,
  };

  const filterByStatus = (item: PlacesWithLocation | OnlineDeliveryman) => {
    if (!hasAnyFilter) return true;

    if ('placeName' in item) {
      return matchPlace(item);
    }

    return matchDeliveryman(item);
  };

  const filterByModel = (item: PlacesWithLocation | OnlineDeliveryman) => {
    if (hasAllModel) return true;

    if ('placeName' in item) {
      return placeRules[item.placeType] ?? false;
    }

    return deliverymanRules[item.deliverymanType] ?? false;
  };

  const combinedFilteredItems = [
    ...placesWithLocation,
    ...onlineDeliverymen,
  ].filter(filterByStatus);

  const combinedDeliveryModelFilteredItems =
    combinedFilteredItems.filter(filterByModel);

  const [positionPlace, setPositionPlace] = useState({
    latitude: -10.2317393,
    longitude: -50.0854409,
  });

  const [loading, setLoading] = useState(false);

  const [zoom, setZoom] = useState(5);

  useEffect(() => {
    if (selectedCityLocation) {
      setLoading(true);
      setPositionPlace({
        latitude: selectedCityLocation.latitude,
        longitude: selectedCityLocation.longitude,
      });
      setZoom(14.5);

      setTimeout(() => setLoading(false), 1000);
    }
  }, [selectedCityLocation]);

  return loading ? (
    <Skeleton height="100%" />
  ) : (
    <>
      <MapContainer
        center={[positionPlace.latitude, positionPlace.longitude]}
        zoom={zoom}
        id="mapid"
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {(combinedDeliveryModelFilteredItems || []).map((item, index) => {
          if ('deliverymanName' in item) {
            return (
              <MarkerDeliveryman deliveryman={item} index={index} key={index} />
            );
          }
          return <MarkerPlace place={item} index={index} key={index} />;
        })}
      </MapContainer>
    </>
  );
};

export default MotoboysMap;
